import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import Ref from "@/components/Ref";
import { CheckCircle2 } from "lucide-react";
import { asArray } from "@/lib/asArray";

/**
 * Batch B · Round B2 · D11 — read-only Response Plan summary rendered
 * at the top of forms 108(a) and 202(a). Data is fed from the Audit
 * Response Plan; nothing in this block is editable in the form.
 */
export default function ResponsePlanSummary({ engagementId }) {
  const colW = useResizableColumns({ storageKey: "response-plan-summary", defaults: [240, 160, 160, 160, 130, 110], mins: [130, 100, 100, 100, 90, 72] });   // Step 11d · add-on
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!engagementId) return;
    api.get(`/engagements/${engagementId}/response-plan`)
      .then((r) => setRows(r.data?.rows || []))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed"));
  }, [engagementId]);

  if (!engagementId) return null;

  return (
    <div className="rounded border border-emerald-200 bg-emerald-50/40 p-3 text-[12px] dark:border-emerald-900 dark:bg-emerald-950/20"
         data-testid="response-plan-summary">
      <div className="mb-2 flex items-center justify-between">
        <div className="font-medium text-emerald-900 dark:text-emerald-100">
          {t("plan.summary.title")}
        </div>
        <div className="text-[10px] text-emerald-800/80 dark:text-emerald-200/80">
          {t("plan.summary.readOnlyNote")}
        </div>
      </div>
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
             data-testid="response-plan-summary-error">
          {error}
        </div>
      )}
      {!error && rows === null && <div data-testid="response-plan-summary-loading">{t("common.loading")}</div>}
      {rows !== null && rows.length === 0 && (
        <div className="text-[11px] text-muted-foreground" data-testid="response-plan-summary-empty">
          {t("plan.empty")}
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <ResizableTable cols={colW} wrap className="w-full text-[11px]">
          <thead className="border-b">
            <tr>
              <ResizableTh index={0} className="px-1 py-1 text-start">{t("plan.summary.risk")}</ResizableTh>
              <ResizableTh index={1} className="px-1 py-1 text-start">{t("plan.summary.fsHead")}</ResizableTh>
              <ResizableTh index={2} className="px-1 py-1 text-start">{t("plan.summary.assertions")}</ResizableTh>
              <ResizableTh index={3} className="px-1 py-1 text-start">{t("plan.summary.significant")}</ResizableTh>
              <ResizableTh index={4} className="px-1 py-1 text-end">{t("plan.summary.plannedCount")}</ResizableTh>
              <ResizableTh index={5} className="px-1 py-1 text-start">{t("plan.summary.status")}</ResizableTh>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.risk_id} data-testid={`rp-sum-row-${r.risk_id}`}>
                <td className="px-1 py-1">{r.risk_title}</td>
                <td className="px-1 py-1 font-mono">
                  {(r.fs_codes || []).length === 0
                    ? <span className="text-muted-foreground">—</span>
                    : asArray(r.fs_codes).map((c) => <Ref key={c}>{c}</Ref>)}
                </td>
                <td className="px-1 py-1">{asArray(r.assertions).join(", ") || "—"}</td>
                <td className="px-1 py-1" data-testid={`rp-sum-sig-${r.risk_id}`}>
                  {r.significant ? t("common.yes") : t("common.no")}
                </td>
                <td className="px-1 py-1 text-end" data-testid={`rp-sum-count-${r.risk_id}`}>
                  {(r.planned || []).length}
                </td>
                <td className="px-1 py-1">
                  {(r.validation?.badges || []).length === 0 ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" data-testid={`rp-sum-ok-${r.risk_id}`} />
                  ) : (
                    asArray(r.validation.badges).map((b, i) => (
                      <span key={`${b.id}-${i}`}
                            className="me-1 rounded bg-red-100 px-1 py-0.5 text-[9px] text-red-800 dark:bg-red-950/40 dark:text-red-300"
                            data-testid={`rp-sum-badge-${r.risk_id}-${b.id}-${i}`}
                            title={t(b.key)}>
                        {b.id}
                      </span>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      )}
    </div>
  );
}
